import { responseData, responsePage } from '@/utils/responseData';
import type { User, DataScopeRule, FieldPermissionRule } from '../../api/types';
import type { TablePaginationConfig } from 'antd';
/**
 * 角色管理列表页面
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Input, Form, Select,
  Modal, message, Tag, Popconfirm, List, Avatar, Spin,
  Segmented, Statistic, Row, Col, Tree, Drawer, Empty,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined,
  SafetyOutlined, CopyOutlined, ApartmentOutlined,
  UnorderedListOutlined, MenuOutlined, TeamOutlined, DatabaseOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { DataNode } from 'antd/es/tree';
import { getRoleList, deleteRole, getRoleTree, getRoleStatistics } from '../../api/role';
import { getRoleDataPermissions } from '../../api/dataPermission';
import { getUserList } from '../../api/user';
import { getAvailableTemplates, applyTemplate } from '../../api/roleTemplate';
import type { Role, RoleQueryParams, RoleTemplate, ApplyTemplateData, RoleStatistics, RoleTreeNode } from '../../api/types';
import RoleForm from './RoleForm';
import RolePermissionTree from './RolePermissionTree';
import RoleMenuTree from './RoleMenuTree';
import RoleInheritanceViz from './RoleInheritanceViz';
import Permission from '@/modules/shared/components/common/Permission';
import {
  PERM_ROLES_CREATE, PERM_ROLES_UPDATE, PERM_ROLES_DELETE,
} from '@/constants/permissions';

const { confirm } = Modal;

// 角色树节点转 Ant Tree DataNode
const convertRoleTreeNodes = (nodes: RoleTreeNode[]): DataNode[] =>
  nodes.map((node) => ({
    key: node.id,
    title: (
      <Space size={4}>
        <span style={{ fontWeight: 500 }}>{node.name}</span>
        <Tag style={{ fontSize: 11 }}>{node.code}</Tag>
        <Tag color={node.status === 1 ? 'success' : 'default'} style={{ fontSize: 11 }}>
          {node.status === 1 ? '启用' : '禁用'}
        </Tag>
        {node.userCount !== undefined && (
          <span style={{ color: '#999', fontSize: 12 }}><TeamOutlined /> {node.userCount}</span>
        )}
      </Space>
    ),
    children: node.children?.length ? convertRoleTreeNodes(node.children) : undefined,
  }));

const collectAllTreeKeys = (nodes: DataNode[]): string[] => {
  const keys: string[] = [];
  const traverse = (list: DataNode[]) => {
    for (const n of list) {
      keys.push(String(n.key));
      if (n.children?.length) traverse(n.children);
    }
  };
  traverse(nodes);
  return keys;
};

const RoleList: React.FC = () => {
  const [list, setList] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [currentRole, setCurrentRole] = useState<Role | null>(null);

  // 权限分配抽屉
  const [permDrawerVisible, setPermDrawerVisible] = useState(false);
  const [permRoleId, setPermRoleId] = useState<string | null>(null);

  // 菜单分配抽屉
  const [menuDrawerVisible, setMenuDrawerVisible] = useState(false);
  const [menuRoleId, setMenuRoleId] = useState<string | null>(null);

  // 继承可视化
  const [inheritanceVisible, setInheritanceVisible] = useState(false);

  // 统计数据
  const [statistics, setStatistics] = useState<RoleStatistics | null>(null);

  // 视图模式: list | tree
  const [viewMode, setViewMode] = useState<'list' | 'tree'>('list');
  const [roleTreeData, setRoleTreeData] = useState<DataNode[]>([]);
  const [treeExpandedKeys, setTreeExpandedKeys] = useState<string[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);

  // 用户列表抽屉
  const [usersDrawerVisible, setUsersDrawerVisible] = useState(false);
  const [_usersRoleId, setUsersRoleId] = useState<string | null>(null);
  const [usersRoleName, setUsersRoleName] = useState('');
  const [roleUsers, setRoleUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // 数据权限抽屉
  const [dataPermDrawerVisible, setDataPermDrawerVisible] = useState(false);
  const [_dataPermRoleId, setDataPermRoleId] = useState<string | null>(null);
  const [dataPermRoleName, setDataPermRoleName] = useState('');
  const [dataPermissions, setDataPermissions] = useState<{ scopes: DataScopeRule[]; fields: FieldPermissionRule[] }>({ scopes: [], fields: [] });
  const [dataPermLoading, setDataPermLoading] = useState(false);

  // 模板选择器弹窗
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [templates, setTemplates] = useState<RoleTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  const [searchForm] = Form.useForm();

  // 获取角色列表
  const fetchRoleList = useCallback(async () => {
    setLoading(true);
    try {
      const formValues = searchForm.getFieldsValue();
      const params: RoleQueryParams = {
        ...formValues,
        current: pagination.current,
        pageSize: pagination.pageSize,
      };

      const res = await getRoleList(params);
      if (res?.data) {
        const { list: items, total: count } = responsePage<Role>(res);
        setList(items);
        setTotal(count);
      }
    } catch (error) {
      console.error('获取角色列表失败', error);
    } finally {
      setLoading(false);
    }
  }, [pagination, searchForm]);

  // 获取统计
  const fetchStatistics = async () => {
    try {
      const res = await getRoleStatistics();
      const data = responseData<RoleStatistics>(res);
      if (data) setStatistics(data);
    } catch {
      // 静默处理
    }
  };

  // 获取角色树
  const fetchRoleTree = async () => {
    setTreeLoading(true);
    try {
      const res = await getRoleTree();
      const nodes = responsePage<RoleTreeNode>(res).list;
      const tree = convertRoleTreeNodes(nodes);
      setRoleTreeData(tree);
      setTreeExpandedKeys(collectAllTreeKeys(tree));
    } catch {
      setRoleTreeData([]);
    } finally {
      setTreeLoading(false);
    }
  };

  // 初始化加载
  useEffect(() => {
    fetchRoleList();
    fetchStatistics();
  }, [fetchRoleList]);

  // 处理表格变更
  const handleTableChange = (paginationInfo: TablePaginationConfig) => {
    setPagination({
      current: paginationInfo.current ?? 1,
      pageSize: paginationInfo.pageSize ?? 20,
    });
  };

  // 搜索
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchRoleList();
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    setPagination({ ...pagination, current: 1 });
    fetchRoleList();
  };

  // 新增角色
  const handleAdd = () => {
    setCurrentRole(null);
    setFormModalVisible(true);
  };

  // 编辑角色
  const handleEdit = (record: Role) => {
    setCurrentRole(record);
    setFormModalVisible(true);
  };

  // 分配权限
  const handleAssignPermissions = (record: Role) => {
    setPermRoleId(record.id);
    setPermDrawerVisible(true);
  };

  // 删除角色
  const handleDelete = async (id: string) => {
    try {
      await deleteRole(id);
      message.success('删除成功');
      fetchRoleList();
    } catch (error) {
      console.error('删除失败', error);
    }
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (!selectedRowKeys.length) {
      message.warning('请至少选择一条记录');
      return;
    }

    confirm({
      title: '确定要删除所选角色吗?',
      icon: <ExclamationCircleOutlined />,
      content: '此操作将永久删除所选角色，是否继续？',
      onOk: async () => {
        try {
          // 逐个删除
          await Promise.all(selectedRowKeys.map((key) => deleteRole(String(key))));
          message.success('删除成功');
          fetchRoleList();
          setSelectedRowKeys([]);
        } catch (error) {
          console.error('删除失败', error);
        }
      },
    });
  };

  // 表单提交成功的回调
  const handleFormSuccess = () => {
    setFormModalVisible(false);
    fetchRoleList();
  };

  // 权限分配成功的回调
  const handlePermissionSuccess = () => {
    setPermDrawerVisible(false);
    fetchRoleList();
  };

  // 分配菜单
  const handleAssignMenus = (record: Role) => {
    setMenuRoleId(record.id);
    setMenuDrawerVisible(true);
  };

  // 菜单分配成功
  const handleMenuSuccess = () => {
    setMenuDrawerVisible(false);
  };

  // 查看角色用户
  const handleViewUsers = async (record: Role) => {
    setUsersRoleId(record.id);
    setUsersRoleName(record.name);
    setUsersDrawerVisible(true);
    setUsersLoading(true);
    try {
      const res = await getUserList({ roleId: record.id, current: 1, pageSize: 100 });
      const items = responsePage<User>(res).list;
      setRoleUsers(items);
    } catch {
      setRoleUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  // 查看角色数据权限
  const handleViewDataPermissions = async (record: Role) => {
    setDataPermRoleId(record.id);
    setDataPermRoleName(record.name);
    setDataPermDrawerVisible(true);
    setDataPermLoading(true);
    try {
      const res = await getRoleDataPermissions(Number(record.id));
      const data = responseData<{ scopes?: DataScopeRule[]; fields?: FieldPermissionRule[] }>(res);
      setDataPermissions({
        scopes: data?.scopes || [],
        fields: data?.fields || [],
      });
    } catch {
      setDataPermissions({ scopes: [], fields: [] });
    } finally {
      setDataPermLoading(false);
    }
  };

  // 视图切换
  const handleViewModeChange = (value: string | number) => {
    if (value !== 'tree' && value !== 'list') return;
    setViewMode(value);
    if (value === 'tree' && roleTreeData.length === 0) {
      fetchRoleTree();
    }
  };

  // 从模板创建 - 打开模板选择器
  const handleOpenTemplateSelector = async () => {
    setTemplateModalVisible(true);
    setLoadingTemplates(true);
    try {
      const res = await getAvailableTemplates();
      if (res?.data) {
        const items = responsePage<RoleTemplate>(res).list;
        setTemplates(items);
      }
    } catch (error) {
      console.error('获取模板列表失败', error);
      message.error('获取模板列表失败');
    } finally {
      setLoadingTemplates(false);
    }
  };

  // 应用模板
  const handleApplyTemplate = async (template: RoleTemplate) => {
    confirm({
      title: `确认从模板创建角色？`,
      content: `将基于模板「${template.name}」创建新角色，包含 ${template.permissionCount} 个权限和 ${template.menuCount} 个菜单。`,
      onOk: async () => {
        setApplyingTemplate(true);
        try {
          const data: ApplyTemplateData = { templateId: template.id };
          await applyTemplate(data);
          message.success(`已从模板「${template.name}」创建角色`);
          setTemplateModalVisible(false);
          fetchRoleList();
        } catch (error) {
          console.error('应用模板失败', error);
          message.error('应用模板失败');
        } finally {
          setApplyingTemplate(false);
        }
      },
    });
  };

  const columns: ColumnsType<Role> = [
    {
      title: '角色名称',
      dataIndex: 'name',
      width: 150,
    },
    {
      title: '角色编码',
      dataIndex: 'code',
      width: 140,
    },
    {
      title: '使用对象',
      dataIndex: 'applicableSubjects',
      width: 140,
      render: (values: string[] | undefined) => values?.length === 1 ? (values[0] === 'customer' ? '客户开户账号' : '员工') : '待明确分类',
    },
    {
      title: '使用范围',
      dataIndex: 'usageScopes',
      width: 180,
      render: (values: string[] | undefined, role: Role) => role.applicableSubjects?.[0] === 'customer' ? '下级客户开户' : (values ?? ['owner_tenant']).map(value => value === 'owner_tenant' ? '本租户' : '全部下级租户').join('、'),
    },
    {
      title: '类型',
      dataIndex: 'type',
      width: 100,
      render: (type) => type || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status) => (
        <Tag color={status === 1 ? 'success' : 'default'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '排序',
      dataIndex: 'sort',
      width: 80,
      render: (sort) => sort ?? '-',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 170,
    },
    {
      title: '操作',
      key: 'action',
      width: 420,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Permission permission={PERM_ROLES_UPDATE}>
            <Button type="link" icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
              编辑
            </Button>
          </Permission>
          <Permission permission={PERM_ROLES_UPDATE}>
            <Button type="link" icon={<SafetyOutlined />} size="small" onClick={() => handleAssignPermissions(record)}>
              权限
            </Button>
          </Permission>
          <Permission permission={PERM_ROLES_UPDATE}>
            <Button type="link" icon={<MenuOutlined />} size="small" onClick={() => handleAssignMenus(record)}>
              菜单
            </Button>
          </Permission>
          <Button type="link" icon={<TeamOutlined />} size="small" onClick={() => handleViewUsers(record)}>
            用户
          </Button>
          <Button type="link" icon={<DatabaseOutlined />} size="small" onClick={() => handleViewDataPermissions(record)}>
            数据权限
          </Button>
          <Permission permission={PERM_ROLES_DELETE}>
            <Popconfirm
              title="确定要删除此角色吗?"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" danger icon={<DeleteOutlined />} size="small">
                删除
              </Button>
            </Popconfirm>
          </Permission>
        </Space>
      ),
    },
  ];

  return (
    <div className="role-list-container">
      {/* 统计卡片 */}
      {statistics && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card><Statistic title="角色总数" value={statistics.total} prefix={<SafetyOutlined />} /></Card>
          </Col>
          <Col span={6}>
            <Card><Statistic title="启用" value={statistics.enabled} valueStyle={{ color: '#3f8600' }} /></Card>
          </Col>
          <Col span={6}>
            <Card><Statistic title="禁用" value={statistics.disabled} valueStyle={{ color: '#cf1322' }} /></Card>
          </Col>
          <Col span={6}>
            <Card><Statistic title="平均权限数" value={statistics.avgPermissions} /></Card>
          </Col>
        </Row>
      )}

      <Card
        title="角色管理"
        bordered={false}
        extra={
          <Space>
            <Segmented
              value={viewMode}
              onChange={handleViewModeChange}
              options={[
                { value: 'list', icon: <UnorderedListOutlined />, label: '列表' },
                { value: 'tree', icon: <ApartmentOutlined />, label: '树形' },
              ]}
              size="small"
            />
            <Button
              icon={<ApartmentOutlined />}
              onClick={() => setInheritanceVisible(true)}
              size="small"
            >
              继承可视化
            </Button>
          </Space>
        }
      >
        {/* 搜索表单 (仅列表视图) */}
        {viewMode === 'list' && (
          <Form
            form={searchForm}
            layout="inline"
            onFinish={handleSearch}
            style={{ marginBottom: 16 }}
          >
            <Form.Item name="name">
              <Input placeholder="角色名称" allowClear style={{ width: 160 }} />
            </Form.Item>
            <Form.Item name="code">
              <Input placeholder="角色编码" allowClear style={{ width: 160 }} />
            </Form.Item>
            <Form.Item name="applicable_subjects">
              <Select placeholder="使用对象" allowClear style={{ width: 160 }} options={[{ label: '员工', value: 'internal_user' }, { label: '客户开户账号', value: 'customer' }]} />
            </Form.Item>
            <Form.Item name="usage_scope">
              <Select placeholder="使用范围" allowClear style={{ width: 180 }} options={[{ label: '本租户', value: 'owner_tenant' }, { label: '全部下级租户', value: 'descendant_tenants' }]} />
            </Form.Item>
            <Form.Item name="status">
              <Select
                placeholder="状态"
                style={{ width: 100 }}
                allowClear
              >
                <Select.Option value={1}>启用</Select.Option>
                <Select.Option value={0}>禁用</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  搜索
                </Button>
                <Button onClick={handleReset}>重置</Button>
              </Space>
            </Form.Item>
          </Form>
        )}

        {/* 工具栏 */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Permission permission={PERM_ROLES_CREATE}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                新增角色
              </Button>
            </Permission>
            <Permission permission={PERM_ROLES_CREATE}>
              <Button
                icon={<CopyOutlined />}
                onClick={handleOpenTemplateSelector}
              >
                从模板创建
              </Button>
            </Permission>
            {viewMode === 'list' && (
              <Permission permission={PERM_ROLES_DELETE}>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleBatchDelete}
                  disabled={!selectedRowKeys.length}
                >
                  批量删除
                </Button>
              </Permission>
            )}
          </Space>
        </div>

        {/* 列表视图 */}
        {viewMode === 'list' && (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={list}
            pagination={{
              ...pagination,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
            }}
            loading={loading}
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
          />
        )}

        {/* 树形视图 */}
        {viewMode === 'tree' && (
          <Spin spinning={treeLoading}>
            {roleTreeData.length > 0 ? (
              <div style={{ padding: '8px 0' }}>
                <Space style={{ marginBottom: 12 }}>
                  <Button size="small" onClick={() => setTreeExpandedKeys(collectAllTreeKeys(roleTreeData))}>展开全部</Button>
                  <Button size="small" onClick={() => setTreeExpandedKeys([])}>收起全部</Button>
                </Space>
                <Tree
                  treeData={roleTreeData}
                  expandedKeys={treeExpandedKeys}
                  onExpand={(keys) => setTreeExpandedKeys(keys as string[])}
                  showLine={{ showLeafIcon: false }}
                  blockNode
                />
              </div>
            ) : (
              !treeLoading && (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                  暂无角色树数据
                </div>
              )
            )}
          </Spin>
        )}
      </Card>

      {/* 角色表单对话框 */}
      <RoleForm
        visible={formModalVisible}
        role={currentRole}
        onCancel={() => setFormModalVisible(false)}
        onSuccess={handleFormSuccess}
      />

      {/* 权限分配抽屉 */}
      <RolePermissionTree
        visible={permDrawerVisible}
        roleId={permRoleId}
        onClose={() => setPermDrawerVisible(false)}
        onSuccess={handlePermissionSuccess}
      />

      {/* 菜单分配抽屉 */}
      <RoleMenuTree
        visible={menuDrawerVisible}
        roleId={menuRoleId}
        onClose={() => setMenuDrawerVisible(false)}
        onSuccess={handleMenuSuccess}
      />

      {/* 角色继承可视化 */}
      <RoleInheritanceViz
        visible={inheritanceVisible}
        onClose={() => setInheritanceVisible(false)}
      />

      {/* 模板选择器弹窗 */}
      <Modal
        title="从模板创建角色"
        open={templateModalVisible}
        onCancel={() => setTemplateModalVisible(false)}
        footer={null}
        width={640}
      >
        <Spin spinning={loadingTemplates}>
          {templates.length > 0 ? (
            <List
              dataSource={templates}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button key="apply"
                      type="primary"
                      size="small"
                      loading={applyingTemplate}
                      onClick={() => handleApplyTemplate(item)}
                    >
                      应用
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        style={{ backgroundColor: item.color || '#1890ff' }}
                        icon={item.icon ? undefined : <SafetyOutlined />}
                      >
                        {item.icon || item.name.charAt(0)}
                      </Avatar>
                    }
                    title={
                      <Space>
                        <span>{item.name}</span>
                        <Tag>{item.code}</Tag>
                        <Tag color="blue">{item.scope}</Tag>
                      </Space>
                    }
                    description={
                      <span>
                        {item.description || '暂无描述'}
                        <span style={{ marginLeft: 16, color: '#999' }}>
                          权限: {item.permissionCount} | 菜单: {item.menuCount}
                        </span>
                      </span>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            !loadingTemplates && (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                暂无可用模板
              </div>
            )
          )}
        </Spin>
      </Modal>

      {/* 角色用户列表抽屉 */}
      <Drawer
        title={`角色用户 - ${usersRoleName}`}
        placement="right"
        width={520}
        open={usersDrawerVisible}
        onClose={() => setUsersDrawerVisible(false)}
      >
        <Spin spinning={usersLoading}>
          {roleUsers.length > 0 ? (
            <Table
              rowKey="id"
              dataSource={roleUsers}
              pagination={false}
              size="small"
              columns={[
                { title: '用户名', dataIndex: 'username', width: 120 },
                { title: '姓名', dataIndex: 'realName', width: 100, render: (v: string) => v || '-' },
                { title: '邮箱', dataIndex: 'email', ellipsis: true },
                {
                  title: '状态', dataIndex: 'status', width: 80,
                  render: (status: number | string) => (
                    <Tag color={status === 1 || status === 'active' ? 'success' : 'default'}>
                      {status === 1 || status === 'active' ? '启用' : '禁用'}
                    </Tag>
                  ),
                },
              ]}
            />
          ) : (
            !usersLoading && <Empty description="该角色暂无用户" />
          )}
        </Spin>
      </Drawer>

      {/* 角色数据权限抽屉 */}
      <Drawer
        title={`数据权限 - ${dataPermRoleName}`}
        placement="right"
        width={600}
        open={dataPermDrawerVisible}
        onClose={() => setDataPermDrawerVisible(false)}
      >
        <Spin spinning={dataPermLoading}>
          {/* 数据范围 */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>
              <DatabaseOutlined style={{ marginRight: 4 }} />
              数据范围规则 {dataPermissions.scopes.length > 0 && <Tag>{dataPermissions.scopes.length}</Tag>}
            </div>
            {dataPermissions.scopes.length > 0 ? (
              <Table
                rowKey="id"
                dataSource={dataPermissions.scopes}
                pagination={false}
                size="small"
                columns={[
                  { title: '资源', dataIndex: 'resource_type', width: 100, render: (v: string) => v || '-' },
                  { title: '范围类型', dataIndex: 'scope_type', width: 100, render: (v: string) => <Tag>{v || '-'}</Tag> },
                  { title: '描述', dataIndex: 'description', ellipsis: true, render: (v: string) => v || '-' },
                ]}
              />
            ) : (
              <div style={{ color: '#999', fontSize: 12 }}>暂无数据范围规则</div>
            )}
          </div>

          {/* 字段权限 */}
          <div>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>
              <SafetyOutlined style={{ marginRight: 4 }} />
              字段权限 {dataPermissions.fields.length > 0 && <Tag>{dataPermissions.fields.length}</Tag>}
            </div>
            {dataPermissions.fields.length > 0 ? (
              <Table
                rowKey="id"
                dataSource={dataPermissions.fields}
                pagination={false}
                size="small"
                columns={[
                  { title: '资源', dataIndex: 'resource_type', width: 100 },
                  { title: '字段', dataIndex: 'field_name', width: 120 },
                  {
                    title: '权限', dataIndex: 'permission', width: 80,
                    render: (v: string) => (
                      <Tag color={v === 'read_write' ? 'green' : v === 'read_only' ? 'blue' : 'default'}>
                        {v === 'read_write' ? '读写' : v === 'read_only' ? '只读' : v === 'hidden' ? '隐藏' : v || '-'}
                      </Tag>
                    ),
                  },
                  { title: '描述', dataIndex: 'description', ellipsis: true, render: (v: string) => v || '-' },
                ]}
              />
            ) : (
              <div style={{ color: '#999', fontSize: 12 }}>暂无字段权限规则</div>
            )}
          </div>

          {dataPermissions.scopes.length === 0 && dataPermissions.fields.length === 0 && !dataPermLoading && (
            <Empty description="该角色暂无数据权限配置" style={{ marginTop: 40 }} />
          )}
        </Spin>
      </Drawer>
    </div>
  );
};

export default RoleList;
